import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { useV2Auth } from '@/context/V2AuthContext';
import { useTranslation } from 'react-i18next';

const CATEGORIES = ['bug', 'idea', 'praise', 'other'];

export default function FeedbackButton() {
  const { user, API_V2 } = useV2Auth();
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('idea');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const focusedRoutes = ['/give', '/give-classic'];
  if (!user || focusedRoutes.includes(location.pathname) || location.pathname.startsWith('/carma/shared/')) return null;

  const catLabel = (c) => t(`feedbackWidget.cat${c.charAt(0).toUpperCase()}${c.slice(1)}`);

  const submit = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_V2}/feedback`, { category, message: message.trim() }, { withCredentials: true });
      toast.success(t('feedbackWidget.success'));
      setMessage(''); setCategory('idea'); setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('feedbackWidget.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        className="fixed bottom-4 right-5 z-40 text-xs text-[var(--muted)] hover:text-[var(--text)] py-2 px-1 transition-colors underline underline-offset-4 decoration-[var(--muted)]/30"
        aria-label={t('feedbackWidget.button')}
      >
        {t('feedbackWidget.button')}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" data-testid="feedback-modal">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--text)]">{t('feedbackWidget.title')}</h2>
                <p className="text-xs text-[var(--muted)] mt-1">{t('feedbackWidget.subtitle')}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} data-testid="feedback-close-btn" className="text-[var(--muted)] hover:text-[var(--text)]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-[var(--muted)]">{t('feedbackWidget.categoryLabel')}</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    data-testid={`feedback-cat-${c}`}
                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${category === c ? 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)] font-medium' : 'border-[var(--border)] bg-white text-[var(--muted)] hover:bg-gray-50'}`}
                  >
                    {catLabel(c)}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-[var(--muted)]">{t('feedbackWidget.messageLabel')}</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                rows={4}
                placeholder={t('feedbackWidget.placeholder')}
                data-testid="feedback-message-input"
                className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm resize-none"
              />
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={sending || !message.trim()}
              data-testid="feedback-submit-btn"
              className="w-full rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-4 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {sending ? t('feedbackWidget.sending') : t('feedbackWidget.submit')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
